// src/pages/Admin/WaypointsBuilder.tsx

import React, { useState, useEffect } from 'react';
import { WayPoint } from '../../globalService/api.model';

export interface WaypointsBuilderProps {
  productId: string;
  disabled: boolean;
  initialWaypoints?: WayPoint[];
  onSaveSuccess?: () => void;
}

type WPForm = {
  is_active: boolean;
  percentage: number;
  title: string;
  text: string;
  image: string;
  video: string;
  quiz_question: string;
  quiz_answer: string;
  quiz_option1: string;
  quiz_option2: string;
  quiz_option3: string;
  quiz_option4: string;
  reward: number;
  order: string;
  sendCertificate: boolean;
  is_img_dwnld: boolean;
  redirectUrl: string;
};

const WaypointsBuilder: React.FC<WaypointsBuilderProps> = ({
  productId,
  disabled,
  initialWaypoints = [],
  onSaveSuccess,
}) => {
  const [existing, setExisting] = useState<WayPoint[]>([]);
  const [newWps, setNewWps] = useState<WPForm[]>([]);
  const [msg, setMsg] = useState<string>('');
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const API =
    process.env.REACT_APP_API_HOST ||
    'https://virtualchallenge-platform-3edceab55a5f.herokuapp.com';

  useEffect(() => {
    setExisting(initialWaypoints);
    setNewWps([]);
    setOpenIdx(null);
    setMsg('');
  }, [initialWaypoints, productId]);

  const blankWaypoint = (): WPForm => ({
    is_active: true,
    percentage: 0,
    title: '',
    text: '',
    image: '',
    video: '',
    quiz_question: '',
    quiz_answer: '',
    quiz_option1: '',
    quiz_option2: '',
    quiz_option3: '',
    quiz_option4: '',
    reward: 0,
    order: '',
    sendCertificate: false,
    is_img_dwnld: false,
    redirectUrl: '',
  });

  const addNew = () => {
    setNewWps(wps => [...wps, blankWaypoint()]);
    setOpenIdx(newWps.length);
  };

  const updateNew = (i: number, key: keyof WPForm, value: any) => {
    setNewWps(wps => {
      const copy = [...wps];
      copy[i] = { ...copy[i], [key]: value };
      return copy;
    });
  };

  const removeNew = (i: number) => {
    setNewWps(wps => wps.filter((_, idx) => idx !== i));
    if (openIdx === i) setOpenIdx(null);
  };

  const toggle = (i: number) =>
    setOpenIdx(openIdx === i ? null : i);

  const submitNew = async () => {
    if (!productId) {
      setMsg('Product ID missing');
      return;
    }
    if (newWps.length === 0) {
      setMsg('No new waypoints to save');
      return;
    }

    const payload = newWps.map(wp => {
      const quiz = wp.quiz_question
        ? {
            question: wp.quiz_question,
            answer: wp.quiz_answer,
            options: {
              '1': wp.quiz_option1,
              '2': wp.quiz_option2,
              '3': wp.quiz_option3,
              '4': wp.quiz_option4,
            },
          }
        : undefined;
      return {
        is_active: wp.is_active,
        percentage: wp.percentage,
        title: wp.title,
        text: wp.text,
        image: wp.image,
        video: wp.video,
        quiz,
        reward: wp.reward,
        order: wp.order.split(',').map(s => s.trim()),
        sendCertificate: wp.sendCertificate,
        is_img_dwnld: wp.is_img_dwnld,
        redirectUrl: wp.redirectUrl,
      };
    });

    try {
      const res = await fetch(`${API}/medalmad/waypoints/${productId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ waypoints: payload }),
      });
      if (!res.ok) throw new Error(await res.text());
      setMsg('New waypoints saved.');
      setNewWps([]);
      setOpenIdx(null);
      onSaveSuccess?.();
    } catch (e: any) {
      setMsg(`Error: ${e.message}`);
    }
  };

  return (
    <div>
      {/* Section: Create New Waypoints */}
      <h5>1) Create New Waypoints</h5>
      <div className="d-flex mb-2">
        <button
          className="btn btn-secondary me-2"
          onClick={addNew}
          disabled={disabled}
        >
          + Add New
        </button>
        <button
          className="btn btn-primary"
          onClick={submitNew}
          disabled={disabled || newWps.length === 0}
        >
          Save New
        </button>
      </div>
      {msg && <div className="alert alert-info">{msg}</div>}

      <div className="accordion mb-4">
        {newWps.map((wp, i) => (
          <div className="accordion-item" key={`new-${i}`}>
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${
                  openIdx === i ? '' : 'collapsed'
                }`}
                onClick={() => toggle(i)}
                type="button"
                disabled={disabled}
              >
                New #{i + 1}: {wp.title || '(no title)'}
              </button>
            </h2>
            <div
              className="accordion-collapse collapse"
              style={{ display: openIdx === i ? 'block' : 'none' }}
            >
              <div className="accordion-body position-relative">
                <button
                  type="button"
                  className="btn-close position-absolute top-0 end-0"
                  onClick={() => removeNew(i)}
                  disabled={disabled}
                />

                {/* --- New waypoint form fields (editable) --- */}
                {/* Active */}
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={wp.is_active}
                    onChange={e =>
                      updateNew(i, 'is_active', e.target.checked)
                    }
                    disabled={disabled}
                  />
                  <label className="form-check-label">Active</label>
                </div>

                {/* Percentage & Title */}
                <div className="row g-2 mb-2">
                  <div className="col-2">
                    <label className="form-label">%</label>
                    <input
                      type="number"
                      className="form-control"
                      value={wp.percentage}
                      onChange={e =>
                        updateNew(i, 'percentage', +e.target.value)
                      }
                      disabled={disabled}
                    />
                  </div>
                  <div className="col-10">
                    <label className="form-label">Title</label>
                    <input
                      className="form-control"
                      value={wp.title}
                      onChange={e =>
                        updateNew(i, 'title', e.target.value)
                      }
                      disabled={disabled}
                    />
                  </div>
                </div>

                {/* Text */}
                <div className="mb-2">
                  <label className="form-label">Text</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={wp.text}
                    onChange={e =>
                      updateNew(i, 'text', e.target.value)
                    }
                    disabled={disabled}
                  />
                </div>

                {/* Image & Video */}
                <div className="row g-2 mb-2">
                  <div className="col">
                    <label className="form-label">Image URL</label>
                    <input
                      className="form-control"
                      value={wp.image}
                      onChange={e =>
                        updateNew(i, 'image', e.target.value)
                      }
                      disabled={disabled}
                    />
                  </div>
                  <div className="col">
                    <label className="form-label">Video ID</label>
                    <input
                      className="form-control"
                      value={wp.video}
                      onChange={e =>
                        updateNew(i, 'video', e.target.value)
                      }
                      disabled={disabled}
                    />
                  </div>
                </div>

                {/* Quiz */}
                <fieldset className="mb-2">
                  <legend className="form-label">Quiz</legend>
                  <input
                    className="form-control mb-1"
                    placeholder="Question"
                    value={wp.quiz_question}
                    onChange={e =>
                      updateNew(i, 'quiz_question', e.target.value)
                    }
                    disabled={disabled}
                  />
                  <div className="row g-1 mb-1">
                    {['quiz_option1', 'quiz_option2', 'quiz_option3', 'quiz_option4'].map(
                      (opt, j) => (
                        <div className="col-3" key={j}>
                          <input
                            className="form-control"
                            placeholder={`Option ${j + 1}`}
                            value={(wp as any)[opt]}
                            onChange={e =>
                              updateNew(i, opt as any, e.target.value)
                            }
                            disabled={disabled}
                          />
                        </div>
                      )
                    )}
                  </div>
                  <label className="form-label">Answer (1–4)</label>
                  <input
                    type="number"
                    className="form-control"
                    min={1}
                    max={4}
                    value={wp.quiz_answer}
                    onChange={e =>
                      updateNew(i, 'quiz_answer', e.target.value)
                    }
                    disabled={disabled}
                  />
                </fieldset>

                {/* Reward */}
                <div className="mb-2">
                  <label className="form-label">Reward</label>
                  <input
                    type="number"
                    className="form-control"
                    value={wp.reward}
                    onChange={e =>
                      updateNew(i, 'reward', +e.target.value)
                    }
                    disabled={disabled}
                  />
                </div>

                {/* Order */}
                <div className="mb-2">
                  <label className="form-label">
                    Order (comma-separated)
                  </label>
                  <input
                    className="form-control"
                    value={wp.order}
                    onChange={e =>
                      updateNew(i, 'order', e.target.value)
                    }
                    disabled={disabled}
                  />
                </div>

                {/* Flags */}
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={wp.sendCertificate}
                    onChange={e =>
                      updateNew(i, 'sendCertificate', e.target.checked)
                    }
                    disabled={disabled}
                  />
                  <label className="form-check-label">
                    Send Certificate
                  </label>
                </div>

                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={wp.is_img_dwnld}
                    onChange={e =>
                      updateNew(i, 'is_img_dwnld', e.target.checked)
                    }
                    disabled={disabled}
                  />
                  <label className="form-check-label">
                    Allow Image Download
                  </label>
                </div>

                {/* Redirect */}
                <div className="mb-2">
                  <label className="form-label">Redirect URL</label>
                  <input
                    type="url"
                    className="form-control"
                    value={wp.redirectUrl}
                    onChange={e =>
                      updateNew(i, 'redirectUrl', e.target.value)
                    }
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Section: Existing Waypoints (read-only) */}
      {existing.length > 0 && (
        <>
          <h5>2) Existing Waypoints</h5>
          <div className="accordion">
            {existing.map((wp, i) => {
              const idx = newWps.length + i;
              return (
                <div className="accordion-item" key={`old-${i}`}>
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${
                        openIdx === idx ? '' : 'collapsed'
                      }`}
                      onClick={() => toggle(idx)}
                      type="button"
                      disabled={disabled}
                    >
                      {i + 1}. {wp.title}
                    </button>
                  </h2>
                  <div
                    className="accordion-collapse collapse"
                    style={{ display: openIdx === idx ? 'block' : 'none' }}
                  >
                    <div className="accordion-body">
                      {/* Disabled form fields to display data */}
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={true}
                          disabled
                        />
                        <label className="form-check-label">Active</label>
                      </div>

                      <div className="row g-2 mb-2">
                        <div className="col-2">
                          <label className="form-label">%</label>
                          <input
                            type="number"
                            className="form-control"
                            value={wp.percentage}
                            disabled
                          />
                        </div>
                        <div className="col-10">
                          <label className="form-label">Title</label>
                          <input
                            className="form-control"
                            value={wp.title}
                            disabled
                          />
                        </div>
                      </div>

                      <div className="mb-2">
                        <label className="form-label">Text</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={wp.text}
                          disabled
                        />
                      </div>

                      <div className="row g-2 mb-2">
                        <div className="col">
                          <label className="form-label">Image URL</label>
                          <input
                            className="form-control"
                            value={wp.image}
                            disabled
                          />
                        </div>
                        <div className="col">
                          <label className="form-label">Video ID</label>
                          <input
                            className="form-control"
                            value={wp.video}
                            disabled
                          />
                        </div>
                      </div>

                      {wp.quiz && (
                        <fieldset className="mb-2">
                          <legend className="form-label">Quiz</legend>
                          <input
                            className="form-control mb-1"
                            value={wp.quiz.question}
                            disabled
                          />
                          <div className="row g-1 mb-1">
                            {Object.entries(wp.quiz.options).map(([k,v])=>(
                              <div className="col-3" key={k}>
                                <input
                                  className="form-control"
                                  value={`${k}: ${v}`}
                                  disabled
                                />
                              </div>
                            ))}
                          </div>
                          <label className="form-label">Answer</label>
                          <input
                            className="form-control"
                            value={wp.quiz.answer}
                            disabled
                          />
                        </fieldset>
                      )}

                      <div className="mb-2">
                        <label className="form-label">Reward</label>
                        <input
                          className="form-control"
                          value={wp.reward}
                          disabled
                        />
                      </div>

                      <div className="mb-2">
                        <label className="form-label">Order</label>
                        <input
                          className="form-control"
                          value={(wp.order || []).join(', ')}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default WaypointsBuilder;
